import { Button, Col, Divider, Input, message, Row, Select, Spin } from "antd";
import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import { UseApiCallV2, UseLazyApiCallV2 } from "hooks";
import { useCurrentUserContext } from "providers";
import { useLanguageTranslator } from "hooks/use-language-translator";
import { DropdownSuffix } from "icons";
import { NoteAttachments } from "../note-attachments";
import { PlusOutlined } from "@ant-design/icons";
import { dateTimeFormatted } from "utils/helpers";
import { ConditionalRenderer } from "components";

export const CreateNewNote = ({
  case_id,
  onCloseModal,
  refetchNotes,
  noteData,
  refetchCount,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFilesUuids, setUploadedFilesUuids] = useState(null);
  const { translator } = useLanguageTranslator();
  const { user } = useCurrentUserContext();
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    recipient: "",
    title: "",
    detail: "",
  });

  useEffect(() => {
    if (noteData) {
      setFormData({
        name: noteData.name || "",
        recipient: noteData.recipient || "",
        title: noteData.title || "",
        detail: noteData.detail || "",
      });
    }
  }, [noteData]);

  const [
    createNewNote,
    {
      data: createNewNoteData,
      isLoading: createNewNoteLoading,
      error: uploadingVideoError,
    },
  ] = UseLazyApiCallV2({
    url: "/add_note",
    method: "POST",
  });
  const {
    data: noteCategoryData,
    isLoading: getNoteCategoryLoading,
    error: noteCategoryError,
    refetch: refetchNoteCategory,
  } = UseApiCallV2({
    url: "/get_note_category",
    method: "POST",
    paramsData: { temp: "all" },
  });

  const [
    addNoteCategory,
    {
      data: addNoteCategoryData,
      isLoading: addNoteCategoryLoading,
      error: addNoteCategoryError,
    },
  ] = UseLazyApiCallV2({
    url: "/add_note_category",
    method: "POST",
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
  function handleChangeTextEditor(content) {
    setFormData((prev) => ({ ...prev, detail: content }));
  }

  function getFileUuidsFromChild(uuids) {
    setUploadedFilesUuids(uuids);
  }

  useEffect(() => {
    if (uploadedFilesUuids && !uploading) {
      createNote(uploadedFilesUuids);
    }
  }, [uploadedFilesUuids, uploading]);

  async function createNote(uploadedFilesUuids) {
    const params = {
      case_id,
      loggedUser: user?.email,
      importance: null,
      note_id: noteData?.noteId || null,
      name: formData?.name || null,
      recipient: formData?.recipient || null,
      title: formData?.title || null,
      detail: formData?.detail || null,
      filenames: uploadedFilesUuids,
      message_id: null,
    };

    try {
      const res = await createNewNote({
        params,
      });
      if (res?.status === "Success") {
        message.success(res?.message);
        setFormData({});
        refetchNotes();
        refetchCount();
        onCloseModal();
      }
    } catch (error) {
      message.error(translator("somethingWentWrong"));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setUploading(true);
  }

  const dropdownListNew = noteCategoryData?.data?.map((item) => ({
    label: item.name,
    value: item?.id,
  }));

  function handleSelect(e, data) {
    setFormData((prev) => ({ ...prev, recipient: data?.label }));
  }

  async function addItem(searchValue) {
    try {
      const res = await addNoteCategory({
        params: {
          category: searchValue,
        },
      });
      if (res?.success) {
        message.success(res?.message);
        setSearchValue("");
        setFormData((prev) => ({ ...prev, recipient: "" }));
        refetchNoteCategory();
      }
    } catch (error) {
      console.log({ error });
    }
  }

  return (
    <Spin spinning={createNewNoteLoading}>
      <div style={{ position: "relative" }}>
        <div className={styles.header}>
          <h3 style={{ fontSize: "20px" }}>
            {noteData
              ? translator("caseDetail.notes.updateNote")
              : translator("caseDetail.notes.createNewNote")}
          </h3>
          <ConditionalRenderer condition={noteData?.lastUpdated}>
            <div className={styles.lastUpdated}>
              {translator("lastUpdated")}:{" "}
              <span>{dateTimeFormatted(noteData?.lastUpdated)}</span>
            </div>
          </ConditionalRenderer>
        </div>
        <form onSubmit={handleSubmit}>
          <Row className={styles.formAndAndAttachment} gutter={8}>
            <Col span={15} style={{ height: "100%" }}>
              <div className={styles.createNewNote}>
                <div className={styles.singleField}>
                  <div className={styles.label}>
                    {translator("caseDetail.notes.name")}
                  </div>
                  <Input
                    value={formData.name}
                    name="name"
                    onChange={handleChange}
                    maxLength={40}
                  />
                </div>
                <div className={styles.singleField}>
                  <div className={styles.label}>
                    {translator("caseDetail.notes.relation")}
                  </div>
                  <div style={{ width: "95%" }}>
                    <Select
                      open={open}
                      onDropdownVisibleChange={setOpen}
                      onSearch={setSearchValue}
                      showSearch
                      filterOption={(
                        input,
                        option, // 👈 filter logic
                      ) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      suffixIcon={
                        <DropdownSuffix
                          style={{
                            transition: "transform 0.25s ease",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      }
                      value={formData.recipient}
                      style={{
                        width: "100%",
                        height: "5.6dvh",
                      }}
                      placeholder=""
                      onChange={handleSelect}
                      options={dropdownListNew}
                      dropdownRender={(menu) => {
                        return (
                          <>
                            {menu}

                            {searchValue &&
                              !dropdownListNew?.includes(searchValue) && (
                                <>
                                  <Divider style={{ margin: "8px 0" }} />
                                  <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => addItem(searchValue)}
                                    style={{ width: "100%", textAlign: "left" }}
                                  >
                                    Add "{searchValue}"
                                  </Button>
                                </>
                              )}
                          </>
                        );
                      }}
                    />
                  </div>
                  {/* <Input
            value={formData.recipient}
            name="recipient"
            onChange={handleChange}
          /> */}
                </div>
                <div className={`${styles.singleField} ${styles.title}`}>
                  <div className={styles.label}>
                    {translator("caseDetail.notes.title")}
                  </div>
                  <Input
                    value={formData.title}
                    name="title"
                    onChange={handleChange}
                    maxLength={40}
                  />
                </div>
                <div className={styles.singleField}>
                  <div className={styles.label} style={{ alignSelf: "start" }}>
                    {translator("caseDetail.notes.detail")}
                  </div>
                  <div className={styles.textEditor}>
                    <ReactQuill
                      theme="snow"
                      value={formData?.detail}
                      onChange={handleChangeTextEditor}
                      placeholder={translator("writeSomething")}
                      // modules={modules}
                      // formats={formats}
                    />
                  </div>
                </div>
              </div>
            </Col>
            <Col span={9} style={{ height: "100%" }}>
              <NoteAttachments
                note_id={noteData?.noteId}
                getFileUuidsFromChild={getFileUuidsFromChild}
                uploading={uploading}
                setUploading={setUploading}
              />
            </Col>
          </Row>
          <div className={styles.buttons}>
            <button
              className={styles.cancelBtn}
              type="button"
              onClick={onCloseModal}
            >
              {translator("caseDetail.notes.cancel")}
            </button>
            <button type="submit">
              {noteData?.noteId
                ? translator("caseDetail.notes.update")
                : translator("caseDetail.notes.add")}
            </button>
          </div>
        </form>
      </div>
    </Spin>
  );
};
